/**
 * Kaiya Solutions Homepage
 * Operational Evidence Infrastructure for Businesses
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, CheckCircle, FileText, 
  Scale, ShoppingCart, Truck, Database, Hash,
  Clock, AlertTriangle, Building2, Users, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NetworkBackground from '../components/ui/NetworkBackground';
import SEO from '../components/SEO';

const Home = () => {
  // Evidence Capture Systems (5 Protection Layers)
  const evidenceCaptureSystems = [
    {
      id: 'vendorguard',
      name: 'VendorGuard',
      evidenceType: 'Vendor compliance evidence',
      description: 'Captures credential uploads, insurance verifications, and compliance status changes.',
      href: '/vendorguard',
      icon: Users,
      color: 'text-teal-400',
      bgColor: 'bg-teal-500/10',
      borderColor: 'border-teal-500/30',
    },
    {
      id: 'proof',
      name: 'PROOF',
      evidenceType: 'Job verification evidence',
      description: 'Records job completion with photos, GPS, timestamps, and customer signatures.',
      href: '/proof',
      icon: CheckCircle,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
    },
    {
      id: 'grantguard',
      name: 'GrantGuard',
      evidenceType: 'Grant compliance evidence',
      description: 'Documents transaction compliance, budget tracking, and SEFA reporting events.',
      href: '/grantguard',
      icon: Scale,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
    },
    {
      id: 'manifestguard',
      name: 'ManifestGuard',
      evidenceType: 'Import documentation evidence',
      description: 'Verifies shipping documents, customs compliance, and release authorizations.',
      href: '/manifestguard',
      icon: Truck,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
    },
    {
      id: 'ecom-protect',
      name: 'ECom Protect',
      evidenceType: 'Dispute defense evidence',
      description: 'Compiles chargeback evidence packages with transaction and fulfillment records.',
      href: '/ecom-protect',
      icon: ShoppingCart,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
    },
  ];

  // Evidence examples for the Evidence Ledger section
  const evidenceExamples = [
    { event: 'Vendor credential uploaded', module: 'VendorGuard' },
    { event: 'Job completion recorded', module: 'PROOF' },
    { event: 'Grant compliance resolution', module: 'GrantGuard' },
    { event: 'Import documentation verified', module: 'ManifestGuard' },
    { event: 'Chargeback dispute detected', module: 'ECom Protect' },
  ];

  // Why Evidence Infrastructure Matters
  const whyEvidenceMatters = [
    {
      title: 'Audits require documentation',
      description: 'When auditors ask for records, the Evidence Ledger provides them.',
      icon: FileText,
    },
    {
      title: 'Disputes require proof',
      description: 'When customers dispute work, you have timestamped verification.',
      icon: CheckCircle,
    },
    {
      title: 'Compliance requires records',
      description: 'When regulators review operations, evidence is already organized.',
      icon: Scale,
    },
  ];

  return (
    <>
      {/* SEO auto-resolves from /src/data/routeMetadata.js for path "/" */}
      <SEO includeOrgSchema={true} />
      
      <div className="min-h-screen">
        {/* HERO SECTION */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-gradient-to-br from-[#0a1929] via-[#0d2137] to-[#143552] overflow-hidden">
          <NetworkBackground className="opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929]/80 via-transparent to-[#0a1929]/60" />
          <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#48a8ab]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-1/4 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#48a8ab]/20 text-[#48a8ab] text-sm font-medium rounded-full mb-6 border border-[#48a8ab]/30">
                <Database className="w-4 h-4" />
                <span>Kaiya Risk Infrastructure</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-headline">
                Operational Evidence Infrastructure for Businesses
              </h1>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-10 max-w-3xl mx-auto">
                Kaiya records critical business events and converts them into verifiable operational evidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/kaiya-platform">
                  <Button className="bg-[#48a8ab] hover:bg-[#3d9194] text-white px-8 py-6 text-lg h-auto shadow-lg shadow-[#48a8ab]/25 hover:shadow-[#48a8ab]/40 transition-all duration-300" data-testid="hero-cta-btn">
                    View Platform Architecture
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/evidence-ledger">
                  <Button variant="outline" className="border-gray-600 text-gray-300 hover:border-[#48a8ab] hover:text-white px-8 py-6 text-lg h-auto transition-all duration-300">
                    Learn About Evidence Ledger
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* THE EVIDENCE LEDGER */}
        <section className="py-20 lg:py-28 bg-gradient-to-b from-[#143552] to-[#0d2137] relative overflow-hidden">
          <NetworkBackground className="opacity-20" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#48a8ab]/20 text-[#48a8ab] text-sm font-medium rounded-full mb-6 border border-[#48a8ab]/30">
                  <Database className="w-4 h-4" />
                  <span>Centralized Evidence System</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  The Evidence Ledger
                </h2>
                <p className="text-lg text-gray-400 mb-6">
                  Every event recorded in Kaiya generates a verification ID and evidence record stored in the centralized ledger. Each record includes timestamp, module source, event type, metadata, and hash verification.
                </p>
                <Link to="/evidence-ledger">
                  <Button variant="outline" className="border-[#48a8ab] text-[#48a8ab] hover:bg-[#48a8ab] hover:text-white">
                    Explore Evidence Ledger
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              
              <div className="bg-[#0a1929] border border-[#1e3a5f] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#1e3a5f]">
                  <Database className="w-5 h-5 text-[#48a8ab]" />
                  <span className="text-white font-medium">Evidence Examples</span>
                </div>
                <div className="space-y-3">
                  {evidenceExamples.map((example, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#0d2137] rounded-lg border border-[#1e3a5f]">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-[#48a8ab] rounded-full" />
                        <span className="text-gray-300 text-sm">{example.event}</span>
                      </div>
                      <span className="text-xs text-gray-500 bg-[#143552] px-2 py-1 rounded">{example.module}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-[#1e3a5f] flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    <span>Hash verified</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>Timestamped</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    <span>Auditable</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EVIDENCE CAPTURE SYSTEMS */}
        <section id="protection-layers" className="py-20 lg:py-28 bg-[#0a1929] relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Evidence Capture Systems
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Each Kaiya module generates evidence events that are written to the Evidence Ledger.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {evidenceCaptureSystems.map((system) => (
                <Link
                  key={system.id}
                  to={system.href}
                  className={`group relative bg-[#0d2137] border ${system.borderColor} rounded-xl p-6 hover:border-opacity-100 transition-all duration-300`}
                  data-testid={`evidence-system-${system.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 ${system.bgColor} rounded-lg`}>
                      <system.icon className={`w-6 h-6 ${system.color}`} />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-1">{system.name}</h3>
                  <p className={`text-sm ${system.color} mb-3`}>{system.evidenceType}</p>
                  <p className="text-gray-400 text-sm mb-4">{system.description}</p>
                  
                  <div className="flex items-center text-[#48a8ab] text-sm font-medium group-hover:text-white transition-colors">
                    Learn More <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/protection-layers">
                <Button variant="outline" className="border-[#48a8ab] text-[#48a8ab] hover:bg-[#48a8ab] hover:text-white">
                  View All Protection Layers
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* WHY EVIDENCE INFRASTRUCTURE MATTERS */}
        <section className="py-20 lg:py-28 bg-gradient-to-b from-[#0a1929] to-[#0d2137] relative overflow-hidden">
          <NetworkBackground className="opacity-20" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Why Evidence Infrastructure Matters
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Kaiya captures the evidence automatically.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {whyEvidenceMatters.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#0d2137] border border-[#1e3a5f] rounded-xl p-8 text-center"
                >
                  <div className="p-4 bg-[#48a8ab]/10 rounded-full inline-block mb-6">
                    <item.icon className="w-8 h-8 text-[#48a8ab]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RISK INTELLIGENCE ENGINE */}
        <section className="py-20 lg:py-28 bg-[#0d2137] relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 text-sm font-medium rounded-full mb-6 border border-purple-500/30">
                <AlertTriangle className="w-4 h-4" />
                <span>Risk Intelligence Engine</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Intelligence From Your Evidence
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                The Risk Intelligence Engine aggregates evidence data to generate risk signals, trend reports, documentation exports, and weekly intelligence reports.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-[#0a1929] border border-[#1e3a5f] rounded-lg p-4 text-left">
                  <p className="text-white font-medium mb-1">Risk Signals</p>
                  <p className="text-gray-500 text-sm">Real-time alerts from evidence patterns</p>
                </div>
                <div className="bg-[#0a1929] border border-[#1e3a5f] rounded-lg p-4 text-left">
                  <p className="text-white font-medium mb-1">Trend Reports</p>
                  <p className="text-gray-500 text-sm">Analysis of compliance over time</p>
                </div>
                <div className="bg-[#0a1929] border border-[#1e3a5f] rounded-lg p-4 text-left">
                  <p className="text-white font-medium mb-1">Documentation Exports</p>
                  <p className="text-gray-500 text-sm">Audit-ready evidence packages</p>
                </div>
                <div className="bg-[#0a1929] border border-[#1e3a5f] rounded-lg p-4 text-left">
                  <p className="text-white font-medium mb-1">Weekly Intelligence</p>
                  <p className="text-gray-500 text-sm">Automated compliance summaries</p>
                </div>
              </div>
              
              <Link to="/risk-intelligence">
                <Button variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white">
                  View Risk Intelligence
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 lg:py-28 bg-[#0a1929] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#48a8ab]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Install Evidence Infrastructure
            </h2>
            <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
              Businesses need to prove what happened during disputes, audits, and compliance reviews. Kaiya captures operational evidence automatically and stores it in a centralized Evidence Ledger.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/risk-assessment">
                <Button className="bg-[#48a8ab] hover:bg-[#3d9194] text-white px-8 py-6 text-lg h-auto shadow-lg shadow-[#48a8ab]/25" data-testid="final-cta-btn">
                  Request Risk Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/kaiya-platform">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:border-[#48a8ab] hover:text-white px-8 py-6 text-lg h-auto">
                  View Platform Architecture
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
